/*
 * MeiMei Cat Management System - Frontend Application
 *
 * Copyright (C) 2024-2026 MeiMei Cat Management Contributors
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Snackbar, Button } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout';
import LoginPage from './components/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import QueryInterface from './components/QueryInterface';
import SchemaBrowser from './components/SchemaBrowser';
import DatabaseStats from './components/DatabaseStats';
import DataManagement from './components/DataManagement';
import MainMenu from './components/MainMenu';
import CatsList from './components/CatsList';
import AngelsList from './components/AngelsList';
import AdoptedList from './components/AdoptedList';
import FreedList from './components/FreedList';
import ExternalCatsList from './components/ExternalCatsList';
import StatisticsPage from './components/StatisticsPage';
import CatFullRecord from './components/CatFullRecord';
import Report from './components/Report';
import Municipalities from './components/Municipalities';
import ExpensesList from './components/ExpensesList';
import UserManagement from './components/UserManagement';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
});

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const CURRENT_VERSION = process.env.REACT_APP_VERSION || '';

function App() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const knownVersion = useRef<string>('');

  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${API_URL}/api/version`);
        const data = await res.json();
        const serverVersion: string = data.version || '';
        if (!knownVersion.current) {
          knownVersion.current = serverVersion;
        } else if (serverVersion && serverVersion !== knownVersion.current) {
          setUpdateAvailable(true);
        }
      } catch {
        // silently ignore network errors
      }
    };
    check();
    const interval = setInterval(check, 5 * 60 * 1000); // check every 5 minutes
    return () => clearInterval(interval);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Navigate to="/cats-list" replace />} />
                      <Route path="/dashboard" element={<MainMenu />} />
                      <Route path="/cats-list" element={<CatsList />} />
                      <Route path="/angels-list" element={<AngelsList />} />
                      <Route path="/adopted-list" element={<AdoptedList />} />
                      <Route path="/freed-list" element={<FreedList />} />
                      <Route path="/external-list" element={<ExternalCatsList />} />
                      <Route path="/cat-record/:catId" element={<CatFullRecord />} />
                      <Route path="/expenses" element={<ExpensesList />} />
                      <Route path="/report" element={<Report />} />
                      <Route path="/statistics" element={<StatisticsPage />} />
                      <Route path="/municipalities" element={<Municipalities />} />
                      <Route path="/data" element={<DataManagement />} />
                      <Route path="/query" element={<QueryInterface />} />
                      <Route path="/schema" element={<SchemaBrowser />} />
                      <Route path="/stats" element={<DatabaseStats />} />
                      <Route path="/users" element={<UserManagement />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
      <Snackbar
        open={updateAvailable}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message="A new version is available"
        action={
          <Button color="inherit" size="small" onClick={() => window.location.reload()}>
            Refresh
          </Button>
        }
      />
    </ThemeProvider>
  );
}

export default App;